import React from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { motion } from 'framer-motion';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface AppointmentsChartProps {
  data: {
    labels: string[];
    completed: number[];
    cancelled: number[];
    pending: number[];
  };
}

export function AppointmentsChart({ data }: AppointmentsChartProps) {
  const chartData = {
    labels: data.labels,
    datasets: [
      {
        label: 'הושלמו',
        data: data.completed,
        backgroundColor: 'rgba(34, 197, 94, 0.8)',
        borderRadius: 8,
        borderSkipped: false,
      },
      {
        label: 'ממתינים',
        data: data.pending,
        backgroundColor: 'rgba(251, 191, 36, 0.8)',
        borderRadius: 8,
        borderSkipped: false,
      },
      {
        label: 'בוטלו',
        data: data.cancelled,
        backgroundColor: 'rgba(239, 68, 68, 0.8)',
        borderRadius: 8,
        borderSkipped: false,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
        align: 'end' as const,
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 12,
            family: 'inherit',
          },
        },
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#1f2937',
        bodyColor: '#1f2937',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        padding: 12,
        displayColors: true,
        callbacks: {
          label: function(context: any) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += context.parsed.y + ' תורים';
            }
            return label;
          }
        }
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        stacked: false,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
          drawBorder: false,
        },
        ticks: {
          stepSize: 1,
          font: {
            size: 11,
          },
        },
      },
      x: {
        stacked: false,
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          font: {
            size: 11,
          },
        },
      },
    },
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
      className="bg-white rounded-2xl p-4 sm:p-6 border border-gray-200 shadow-sm"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900">תורים לפי יום</h3>
        <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500">
          <span>החודש הנבחר</span>
        </div>
      </div>
      <div className="h-48 sm:h-64">
        <Bar data={chartData} options={options} />
      </div>
    </motion.div>
  );
}
